import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useRepoParams } from '../../hooks/useRepoParams';
import { useRepoContext, resolveDefaultBranch } from '../../contexts/RepoContext';
import { repoApi, get, del } from '../../api/client';
import { refToSegment } from '../../utils/refSegment';
import { useAuthStore } from '../../stores/auth';
import { EmptyRepoGuide } from '../../components/EmptyRepoGuide';
import { MembersManager } from '../../components/MembersManager';
import { RepoCodeLayout } from '../../components/repo/RepoCodeLayout';
import './RepoPage.css';

interface Repository {
    id: string;
    name: string;
    fullPath: string;
    ownerType: string;
    ownerId: string;
    description?: string;
    isPrivate: boolean;
    defaultBranch: string;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    canAdmin?: boolean;
}

interface Branch {
    name: string;
    sha: string;
    isDefault: boolean;
    updatedAt: string;
}

interface Tag {
    name: string;
    sha: string;
    updatedAt: string;
}

interface TreeItem {
    name: string;
    path: string;
    sha: string;
    type: 'tree' | 'blob';
    mode: string;
}


export function RepoPage() {
    const params = useParams();
    const repoContext = useRepoContext();
    const { owner, repo: repoName } = useRepoParams();
    // Support wildcard path OR legacy owner/repo params; when inside RepoLayout use context repoPath
    const path = repoContext?.repoPath ?? ((owner && repoName) ? `${owner}/${repoName}` : params['*']);
    const { isAuthenticated, sessionId } = useAuthStore();

    const [repo, setRepo] = useState<Repository | null>(null);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentBranchLocal, setCurrentBranchLocal] = useState('');
    const currentBranch = repoContext?.currentBranch ?? currentBranchLocal;
    const setCurrentBranch = repoContext?.setCurrentBranch ?? setCurrentBranchLocal;
    const [activeTab, setActiveTab] = useState<'code' | 'branches' | 'settings'>('code');
    const [treeItems, setTreeItems] = useState<TreeItem[]>([]);
    const [treeLoading, setTreeLoading] = useState(false);
    const [currentPath, setCurrentPath] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (path && !repoContext) {
            loadRepo();
        }
    }, [path]);

    useEffect(() => {
        if (repoContext?.repo) {
            setRepo(repoContext.repo);
            setBranches(repoContext.branches);
            setCurrentBranchLocal(repoContext.currentBranch);
            setLoading(false);
            setError(repoContext.error ?? null);
        }
    }, [repoContext?.repo, repoContext?.currentBranch]);

    const loadRepo = async () => {
        try {
            setLoading(true);
            setError(null);

            const [fetchedRepo, branchesData, tagsData] = await Promise.all([
                repoApi.get(path!),
                repoApi.getBranches(path!).catch(() => []),
                repoApi.getTags(path!).catch(() => []),
            ]);

            setRepo(fetchedRepo);
            setBranches(branchesData);
            setTags(tagsData);
            setCurrentBranch(resolveDefaultBranch(fetchedRepo.defaultBranch, branchesData || []));
        } catch (err: any) {
            setError(err.message || '加载仓库失败');
        } finally {
            setLoading(false);
        }
    };

    // 加载文件树
    const loadTree = async (treePath: string = '') => {
        if (!repo || branches.length === 0 || !currentBranch) return;

        try {
            setTreeLoading(true);
            const refEnc = encodeURIComponent(currentBranch);
            const endpoint = treePath
                ? `/repos/${path}/tree/${refEnc}/${treePath}`
                : `/repos/${path}/tree/${refEnc}`;
            const data = await get<{ items: TreeItem[] }>(endpoint);
            setTreeItems(data.items || []);
        } catch (err) {
            console.error('加载文件树失败:', err);
            setTreeItems([]);
        } finally {
            setTreeLoading(false);
        }
    };

    // 切换分支
    const handleBranchSwitch = (branchName: string) => {
        setCurrentBranch(branchName);
        setCurrentPath('');
        setShowBranchDropdown(false);
    };

    // 当仓库和分支加载完成后，加载文件树（仅非 RepoLayout 时：RepoLayout 下由 RepoCodeLayout 内 RepoFileTree 负责，避免重复请求）
    useEffect(() => {
        if (repoContext) return;
        if (repo && branches.length > 0 && currentBranch) {
            loadTree(currentPath);
        }
    }, [repoContext, repo, branches, currentPath, currentBranch]);

    // 导航到指定路径
    const navigateToPath = (newPath: string) => {
        setCurrentPath(newPath);
    };

    // 返回上级目录
    const navigateUp = () => {
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop();
        setCurrentPath(parts.join('/'));
    };

    // 处理文件/文件夹点击
    const handleItemClick = (item: TreeItem) => {
        if (item.type === 'tree') {
            setCurrentPath(item.path);
        } else {
            // 导航到文件查看页面
            navigate(`/${path}/-/blob/${refToSegment(currentBranch)}/${item.path}`);
        }
    };

    // 删除仓库
    const handleDeleteRepo = async () => {
        if (!repo || !path) return;

        const confirmed = window.confirm(
            `确定要删除仓库 "${repo.name}" 吗？\n\n此操作不可恢复，所有代码和历史记录都将被永久删除。`
        );

        if (!confirmed) return;

        try {
            setDeleting(true);
            await repoApi.delete(path);
            navigate('/dashboard');
        } catch (err: any) {
            alert(err.message || '删除仓库失败');
        } finally {
            setDeleting(false);
        }
    };

    // 删除分支
    const handleDeleteBranch = async (branchName: string) => {
        if (!path) return;

        const confirmed = window.confirm(`确定要删除分支 "${branchName}" 吗？`);
        if (!confirmed) return;

        try {
            await del(`/repos/${path}/branches/${branchName}`);
            // 重新加载分支列表
            const branchesData = await repoApi.getBranches(path);
            setBranches(branchesData);
        } catch (err: any) {
            alert(err.message || '删除分支失败');
        }
    };

    if (loading) {
        return (
            <div className="repo-page">
                <div className="loading">加载中...</div>
            </div>
        );
    }

    if (error || !repo) {
        return (
            <div className="repo-page">
                <div className="error-state">
                    <h2>无法访问该仓库</h2>
                    <p>{error || '仓库不存在或您没有访问权限'}</p>
                    <Link to="/" className="btn btn-primary">
                        返回首页
                    </Link>
                </div>
            </div>
        );
    }

    // 后端根 URL 来自环境变量，实现前后端分离，无硬编码域名
    const getApiOrigin = (): string => {
        const apiBase = import.meta.env.VITE_API_BASE;
        if (apiBase && typeof apiBase === 'string' && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
            try {
                return new URL(apiBase).origin;
            } catch {
                return window.location.origin;
            }
        }
        return window.location.origin;
    };

    const getCloneUrl = () => {
        return `${getApiOrigin()}/${repo.fullPath}.git`;
    };
    const getDownloadUrl = (format: string) => {
        const apiBase = import.meta.env.VITE_API_BASE || '/api/v1';
        const tokenParam = sessionId ? `?token=${sessionId}` : '';
        return `${apiBase}/repos/${repo.fullPath}/archive/${currentBranch}.${format}${tokenParam}`;
    };

    const cloneUrl = getCloneUrl();
    const inRepoLayout = !!repoContext;

    return (
        <div className="repo-page">
            {!inRepoLayout && (
            <>
            {/* 仓库头部 */}
            <div className="repo-header">
                <div className="repo-title">
                    <div className="repo-icon">
                        <svg viewBox="0 0 16 16" width="24" height="24">
                            <path
                                fill="currentColor"
                                d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z"
                            />
                        </svg>
                    </div>
                    <div>
                        <h1>
                            <Link to={`/groups/${repo.fullPath.split('/')[0]}`} className="path-link">
                                {repo.fullPath.split('/')[0]}
                            </Link>
                            <span className="path-separator">/</span>
                            <span className="repo-name">{repo.name}</span>
                        </h1>
                        {repo.description && <p className="repo-description">{repo.description}</p>}
                    </div>
                </div>
                <div className="repo-actions">
                    {repo.isPrivate && <span className="private-badge">私有</span>}

                    {/* 下载按钮 */}
                    <div className="dropdown-wrapper">
                        <button
                            className="btn btn-secondary"
                            onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                        >
                            <svg viewBox="0 0 16 16" width="16" height="16">
                                <path
                                    fill="currentColor"
                                    d="m8.39 12.648 4.244-4.243c.186-.18.183-.481-.013-.67-.196-.189-.49-.187-.673-.003L8.75 10.93V2.75a.474.474 0 0 0-.476-.473.479.479 0 0 0-.476.473v8.177L4.607 7.726a.477.477 0 0 0-.669.003c-.2.189-.197.49-.013.67l4.244 4.243c.187.187.49.19.676.002v.003l.001-.001Z"
                                />
                            </svg>
                            下载
                            <svg viewBox="0 0 16 16" width="12" height="12">
                                <path fill="currentColor" d="m4.427 7.427 3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 7H4.604a.25.25 0 0 0-.177.427Z" />
                            </svg>
                        </button>
                        {showDownloadDropdown && (
                            <div className="dropdown-menu">
                                <div className="dropdown-header">下载源代码</div>
                                <a
                                    href={getDownloadUrl('zip')}
                                    className="dropdown-item"
                                    onClick={() => setShowDownloadDropdown(false)}
                                >
                                    zip
                                </a>
                                <a
                                    href={getDownloadUrl('tar.gz')}
                                    className="dropdown-item"
                                    onClick={() => setShowDownloadDropdown(false)}
                                >
                                    tar.gz
                                </a>
                            </div>
                        )}
                    </div>

                    {/* 克隆按钮 */}
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCloneModal(true)}
                    >
                        <svg viewBox="0 0 16 16" width="16" height="16">
                            <path
                                fill="currentColor"
                                d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25Z"
                            />
                            <path
                                fill="currentColor"
                                d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25Zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25Z"
                            />
                        </svg>
                        克隆
                    </button>
                </div>
            </div>

            {/* 克隆 URL */}
            <div className="clone-section">
                <div className="clone-input-group">
                    <span className="clone-label">HTTPS</span>
                    <input type="text" value={cloneUrl} readOnly className="clone-input" />
                    <button
                        className="copy-btn"
                        onClick={() => navigator.clipboard.writeText(cloneUrl)}
                        title="复制"
                    >
                        <svg viewBox="0 0 16 16" width="16" height="16">
                            <path
                                fill="currentColor"
                                d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25ZM5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25Zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25Z"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* 标签页 */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'code' ? 'active' : ''}`}
                    onClick={() => setActiveTab('code')}
                >
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M4.72 3.22a.749.749 0 0 1 1.06 0l4.25 4.25a.749.749 0 0 1 0 1.06l-4.25 4.25a.749.749 0 1 1-1.06-1.06L8.19 8 4.72 4.28a.749.749 0 0 1 0-1.06Z"
                        />
                    </svg>
                    代码
                </button>
                <button
                    className={`tab ${activeTab === 'branches' ? 'active' : ''}`}
                    onClick={() => setActiveTab('branches')}
                >
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Zm-6 0a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Zm8.25-.75a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM4.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z"
                        />
                    </svg>
                    分支 ({branches.length})
                </button>
                <Link
                    to={`/${repo.fullPath}/-/commits/${refToSegment(currentBranch || repo.defaultBranch || 'main')}`}
                    className="tab"
                >
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M11.93 8.5a4.002 4.002 0 0 1-7.86 0H.75a.75.75 0 0 1 0-1.5h3.32a4.002 4.002 0 0 1 7.86 0h3.32a.75.75 0 0 1 0 1.5Zm-1.43-.75a2.5 2.5 0 1 0-5 0 2.5 2.5 0 0 0 5 0Z"
                        />
                    </svg>
                    历史
                </Link>
                <Link
                    to={`/${repo.fullPath}/-/pulls`}
                    className="tab"
                >
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M1.5 3a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V3zm5 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V3zm5 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V3zM1.5 9a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V9zm5 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V9zm5 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V9z"
                        />
                        <path fill="currentColor" d="M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878zm3.75 7.378a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm3-8.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" />
                    </svg>
                    合并请求
                </Link>
                {isAuthenticated && (
                    <button
                        className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <svg viewBox="0 0 16 16" width="16" height="16">
                            <path
                                fill="currentColor"
                                d="M8 0a8.2 8.2 0 0 1 .701.031C9.444.095 9.99.645 10.16 1.29l.288 1.107c.018.066.079.158.212.224.231.114.454.243.668.386.123.082.233.09.299.071l1.103-.303c.644-.176 1.392.021 1.82.63.27.385.506.792.704 1.218.315.675.111 1.422-.364 1.891l-.814.806c-.049.048-.098.147-.088.294.016.257.016.515 0 .772-.01.147.039.246.088.294l.814.806c.475.469.679 1.216.364 1.891a7.977 7.977 0 0 1-.704 1.217c-.428.61-1.176.807-1.82.63l-1.102-.302c-.067-.019-.177-.011-.3.071a5.909 5.909 0 0 1-.668.386c-.133.066-.194.158-.211.224l-.29 1.106c-.168.646-.715 1.196-1.458 1.26a8.006 8.006 0 0 1-1.402 0c-.743-.064-1.289-.614-1.458-1.26l-.289-1.106c-.018-.066-.079-.158-.212-.224a5.738 5.738 0 0 1-.668-.386c-.123-.082-.233-.09-.299-.071l-1.103.303c-.644.176-1.392-.021-1.82-.63a8.12 8.12 0 0 1-.704-1.218c-.315-.675-.111-1.422.363-1.891l.815-.806c.05-.048.098-.147.088-.294a6.214 6.214 0 0 1 0-.772c.01-.147-.038-.246-.088-.294l-.815-.806C.635 6.045.431 5.298.746 4.623a7.92 7.92 0 0 1 .704-1.217c.428-.61 1.176-.807 1.82-.63l1.102.302c.067.019.177.011.3-.071.214-.143.437-.272.668-.386.133-.066.194-.158.211-.224l.29-1.106C6.009.645 6.556.095 7.299.03 7.53.01 7.764 0 8 0Zm-.571 1.525c-.036.003-.108.036-.137.146l-.289 1.105c-.147.561-.549.967-.998 1.189-.173.086-.34.183-.5.29-.417.278-.97.423-1.529.27l-1.103-.303c-.109-.03-.175.016-.195.045-.22.312-.412.644-.573.99-.014.031-.021.11.059.19l.815.806c.411.406.562.957.53 1.456a4.709 4.709 0 0 0 0 .582c.032.499-.119 1.05-.53 1.456l-.815.806c-.081.08-.073.159-.059.19.162.346.353.677.573.989.02.03.085.076.195.046l1.102-.303c.56-.153 1.113-.008 1.53.27.161.107.328.204.501.29.447.222.85.629.997 1.189l.289 1.105c.029.109.101.143.137.146a6.6 6.6 0 0 0 1.142 0c.036-.003.108-.036.137-.146l.289-1.105c.147-.561.549-.967.998-1.189.173-.086.34-.183.5-.29.417-.278.97-.423 1.529-.27l1.103.303c.109.029.175-.016.195-.045.22-.313.411-.644.573-.99.014-.031.021-.11-.059-.19l-.815-.806c-.411-.406-.562-.957-.53-1.456a4.709 4.709 0 0 0 0-.582c-.032-.499.119-1.05.53-1.456l.815-.806c.081-.08.073-.159.059-.19a6.464 6.464 0 0 0-.573-.989c-.02-.03-.085-.076-.195-.046l-1.102.303c-.56.153-1.113.008-1.53-.27a4.44 4.44 0 0 0-.501-.29c-.447-.222-.85-.629-.997-1.189l-.289-1.105c-.029-.11-.101-.143-.137-.146a6.6 6.6 0 0 0-1.142 0ZM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM9.5 8a1.5 1.5 0 1 0-3.001.001A1.5 1.5 0 0 0 9.5 8Z"
                            />
                        </svg>
                        设置
                    </button>
                )}
            </div>
            </>
            )}

            {/* 内容区 */}
            <div className="tab-content">
                {(inRepoLayout || activeTab === 'code') && (
                    <div className="code-tab">
                        {branches.length === 0 ? (
                            <EmptyRepoGuide
                                repoName={repo.name}
                                repoFullPath={repo.fullPath}
                                baseUrl={getApiOrigin()}
                            />
                        ) : (() => {
                            const codeContent = (
                                <>
                                    <div className="branch-selector">
                                        <div className="dropdown-wrapper">
                                            <button
                                                className="branch-btn"
                                                onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                                            >
                                                <svg viewBox="0 0 16 16" width="16" height="16">
                                                    <path
                                                        fill="currentColor"
                                                        d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Z"
                                                    />
                                                </svg>
                                                {currentBranch || repo.defaultBranch}
                                                <svg viewBox="0 0 16 16" width="12" height="12">
                                                    <path fill="currentColor" d="m4.427 7.427 3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 7H4.604a.25.25 0 0 0-.177.427Z" />
                                                </svg>
                                            </button>
                                            {showBranchDropdown && (
                                                <div className="dropdown-menu branch-dropdown">
                                                    <div className="dropdown-header">分支</div>
                                                    {branches.map((branch) => (
                                                        <button
                                                            key={branch.name}
                                                            className={`dropdown-item ${branch.name === currentBranch ? 'active' : ''}`}
                                                            onClick={() => handleBranchSwitch(branch.name)}
                                                        >
                                                            {branch.name}
                                                            {branch.isDefault && <span className="default-badge">默认</span>}
                                                        </button>
                                                    ))}
                                                    {tags.length > 0 && (
                                                        <>
                                                            <div className="dropdown-divider"></div>
                                                            <div className="dropdown-header">标签</div>
                                                            {tags.map((tag) => (
                                                                <button
                                                                    key={tag.name}
                                                                    className={`dropdown-item ${tag.name === currentBranch ? 'active' : ''}`}
                                                                    onClick={() => handleBranchSwitch(tag.name)}
                                                                >
                                                                    <span className="tag-icon">🏷️</span> {tag.name}
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {currentPath && (
                                            <div className="breadcrumbs">
                                                <button onClick={() => navigateToPath('')}>根目录</button>
                                                {currentPath.split('/').filter(Boolean).map((part, i, arr) => (
                                                    <span key={i}>
                                                        <span> / </span>
                                                        <button onClick={() => navigateToPath(arr.slice(0, i + 1).join('/'))}>
                                                            {part}
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="file-tree">
                                        {treeLoading ? (
                                            <div className="tree-loading">加载文件列表...</div>
                                        ) : treeItems.length === 0 ? (
                                            <div className="tree-empty">暂无文件</div>
                                        ) : (
                                            <div className="file-list">
                                                {currentPath && (
                                                    <div className="file-item" onClick={() => navigateUp()}>
                                                        <span className="file-icon folder">📁</span>
                                                        <span className="file-name">..</span>
                                                    </div>
                                                )}
                                                {treeItems.map((item) => (
                                                    <div
                                                        key={item.path}
                                                        className="file-item"
                                                        onClick={() => handleItemClick(item)}
                                                    >
                                                        <span className={`file-icon ${item.type}`}>
                                                            {item.type === 'tree' ? '📁' : '📄'}
                                                        </span>
                                                        <span className="file-name">{item.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                            return inRepoLayout ? (
                                <RepoCodeLayout
                                    repoPath={path!}
                                    refName={currentBranch || repo.defaultBranch || 'main'}
                                    treePath={currentPath}
                                >
                                    {codeContent}
                                </RepoCodeLayout>
                            ) : codeContent;
                        })()}
                    </div>
                )}

                {!inRepoLayout && activeTab === 'branches' && (
                    <div className="branches-tab">
                        {branches.length === 0 ? (
                            <div className="empty-section">
                                <p>还没有分支</p>
                            </div>
                        ) : (
                            <div className="branch-list">
                                {branches.map((branch) => (
                                    <div key={branch.name} className="branch-row">
                                        <div className="branch-info">
                                            <span className="branch-name">
                                                {branch.name || '(unnamed branch)'}
                                                {branch.isDefault && <span className="default-badge">默认</span>}
                                            </span>
                                            <span className="branch-sha">{branch.sha?.substring(0, 7) || 'N/A'}</span>
                                        </div>
                                        <div className="branch-actions">
                                            {!branch.isDefault && isAuthenticated && (
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDeleteBranch(branch.name)}
                                                    title="删除分支"
                                                >
                                                    <svg viewBox="0 0 16 16" width="14" height="14">
                                                        <path fill="currentColor" d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75Zm-5.5 0V3h5V1.75a.25.25 0 0 0-.25-.25h-4.5a.25.25 0 0 0-.25.25ZM4.997 6.178a.75.75 0 1 0-1.493.144L4.217 13.5a1.75 1.75 0 0 0 1.743 1.5h4.08a1.75 1.75 0 0 0 1.743-1.5l.713-7.178a.75.75 0 1 0-1.493-.144z" />
                                                    </svg>
                                                    删除
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!inRepoLayout && activeTab === 'settings' && (
                    <div className="settings-tab">
                        <section className="settings-section">
                            <h3>仓库设置</h3>
                            <div className="setting-item">
                                <label>默认分支</label>
                                <select defaultValue={repo.defaultBranch}>
                                    {branches.map((b) => (
                                        <option key={b.name} value={b.name}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="setting-item">
                                <label>
                                    <input type="checkbox" checked={repo.isPrivate} readOnly />
                                    私有仓库
                                </label>
                            </div>
                        </section>

                        <section className="settings-section">
                            <MembersManager repoPath={repo.fullPath} canManage={repo.canAdmin === true} />
                        </section>

                        <section className="settings-section danger">
                            <h3>危险区域</h3>
                            <div className="danger-item">
                                <div>
                                    <strong>删除仓库</strong>
                                    <p>删除后无法恢复，请谨慎操作</p>
                                </div>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteRepo}
                                    disabled={deleting}
                                >
                                    {deleting ? '删除中...' : '删除仓库'}
                                </button>
                            </div>
                        </section>
                    </div>
                )}
            </div>

            {/* 克隆弹窗 */}
            {showCloneModal && (
                <div className="modal-overlay" onClick={() => setShowCloneModal(false)}>
                    <div className="modal-content clone-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>克隆仓库</h3>
                            <button className="modal-close" onClick={() => setShowCloneModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="clone-option">
                                <h4>使用 HTTPS 克隆</h4>
                                <div className="clone-input-group">
                                    <input
                                        type="text"
                                        value={cloneUrl}
                                        readOnly
                                        className="clone-input"
                                    />
                                    <button
                                        className="copy-btn"
                                        onClick={() => {
                                            navigator.clipboard.writeText(cloneUrl);
                                            alert('已复制到剪贴板');
                                        }}
                                        title="复制"
                                    >
                                        <svg viewBox="0 0 16 16" width="16" height="16">
                                            <path
                                                fill="currentColor"
                                                d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25ZM5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25Zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25Z"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="clone-command">
                                <code>git clone {cloneUrl}</code>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RepoPage;
